import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { DoubleSide } from 'three';
import { createBrickTextures, createPlankTextures } from '../../utils/roomTextures';

/**
 * The floor and wall of the simulated preview room, in real-world metres.
 *
 * Shared because two previews need the same place: Designer's synthetic camera stages a tracking
 * image in it, and JUTE's world-tracking simulator drops scene content into it at the origin. Only
 * the surfaces are here — anything either preview puts *in* the room stays with that preview.
 *
 * `unitsPerMetre` is the whole bridge between these dimensions and the scene, and the two callers
 * disagree on it: the synthetic camera scales the room to the target's printed height, and JUTE's
 * non-AR mode renders at twice AR scale. Passing it in is what lets one component serve both.
 */

/** Tile sizes in metres, so the texture repeats come out at believable brick and plank dimensions.
 *  A standard brick is 215x65mm on a 10mm bed: two per tile across, four courses down. */
const BRICK_TILE_W_M = 2 * (0.215 + 0.01);
const BRICK_TILE_H_M = 4 * (0.065 + 0.01);
/** Four boards across the tile at 60mm each; two rows down it at 500mm board length. */
const PLANK_TILE_W_M = 4 * 0.06;
const PLANK_TILE_H_M = 2 * 0.5;


interface IParentProps {
	/** Scene units per real-world metre. */
	unitsPerMetre: number;
	/** Edge length of the plank floor, in metres. Omit to leave the floor out. */
	floorSizeM?: number;
	/** Edge length of the brick wall, in metres. Omit to leave the wall out. */
	wallSizeM?: number;
	/** How far the floor sits below y=0, in metres, so it never z-fights whatever rests on it. */
	floorStandoffM?: number;
	/** How far the wall sits behind z=0, in metres. */
	wallStandoffM?: number;
	/**
	 * Scales how large each floorboard appears. 1 keeps them at the real-world size the tile is drawn
	 * for; 2 makes them twice as wide and twice as long, so the floor repeats half as often.
	 *
	 * Exists because only one caller has a real-world scale to be faithful to. The synthetic camera
	 * sizes its room from the target's printed height, so boards there should measure what they claim.
	 * A world-tracked scene has no printed reference at all, and at that room's size real 60mm boards
	 * read as fine stripes from the distance you orbit at.
	 */
	floorTileScale?: number;
	/**
	 * Draws both surfaces in their dark colourway. For content bright enough to disappear against the
	 * pastel room — the room is a backdrop to judge content against, and a backdrop that wins is no use.
	 *
	 * Redrawn, not tinted: the textures come out dark, and nothing else about the room changes.
	 */
	dark?: boolean;
}

const RoomSurfaces: FunctionComponent<IParentProps> = ({
	unitsPerMetre,
	floorSizeM,
	wallSizeM,
	floorStandoffM = 0.01,
	wallStandoffM = 0.01,
	floorTileScale = 1,
	dark = false
}) => {
	// Built lazily and only for the surfaces actually asked for: the synthetic camera stages a target
	// on one or the other, never both, so building the unused set would be pure waste.
	// A bigger tile covers the same floor in fewer repeats, which is what makes each board larger.
	const planks = useMemo(
		() =>
			floorSizeM
				? createPlankTextures(
						floorSizeM / (PLANK_TILE_W_M * floorTileScale),
						floorSizeM / (PLANK_TILE_H_M * floorTileScale),
						dark
					)
				: null,
		[floorSizeM, floorTileScale, dark]
	);
	const brick = useMemo(
		() => (wallSizeM ? createBrickTextures(wallSizeM / BRICK_TILE_W_M, wallSizeM / BRICK_TILE_H_M, dark) : null),
		[wallSizeM, dark]
	);

	// Each of these is a megabyte-scale canvas on the GPU, and switching colourway builds a new pair
	// every time. Without this the discarded ones are never freed, so toggling leaks the room.
	useEffect(
		() => () => {
			planks?.map.dispose();
			planks?.bump.dispose();
		},
		[planks]
	);
	useEffect(
		() => () => {
			brick?.map.dispose();
			brick?.bump.dispose();
		},
		[brick]
	);

	// Both surfaces are double-sided: orbiting is unbounded, and a single-sided plane simply vanishes
	// when you pass behind it.
	return (
		<>
			{!!floorSizeM && (
				<mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -floorStandoffM * unitsPerMetre, 0]}>
					<planeGeometry args={[floorSizeM * unitsPerMetre, floorSizeM * unitsPerMetre]} />
					{/* Only the seams between boards recede in the bump map; the grain and knots are
					    colour, because they change how a board looks rather than its shape. */}
					<meshStandardMaterial
						map={planks?.map}
						bumpMap={planks?.bump}
						bumpScale={0.8}
						side={DoubleSide}
						roughness={0.8}
						metalness={0}
					/>
				</mesh>
			)}

			{!!wallSizeM && (
				<mesh receiveShadow position={[0, 0, -wallStandoffM * unitsPerMetre]}>
					<planeGeometry args={[wallSizeM * unitsPerMetre, wallSizeM * unitsPerMetre]} />
					{/* The brick carries its own bump map — the mortar is the darkest thing in it,
					    which is exactly where the surface should recede. */}
					<meshStandardMaterial
						map={brick?.map}
						bumpMap={brick?.bump}
						bumpScale={1.2}
						side={DoubleSide}
						roughness={0.95}
						metalness={0}
					/>
				</mesh>
			)}
		</>
	);
};

RoomSurfaces.displayName = 'RoomSurfaces';
export default RoomSurfaces;
